import {
  createColumnHelper,
  getCoreRowModel,
  getFacetedMinMaxValues,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import PaginatedTable from "../Common/PaginatedTable";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { HiEye, HiPencilAlt } from "react-icons/hi";
import { Button, Checkbox, Tooltip } from "flowbite-react";
import { DisplayAd } from "../../Models/DisplayAd";
import { displayDateOnly } from "../../Utils";
import { useAppSelector } from "../../Reducers/store";
import { UserLevelNames } from "../../Models/UserLevel";
import { toast } from "react-toastify";

export default function MemberAdsList({
  data,
  setSelectedDisplayAd,
  setShowDisplayAdModal,
}: {
  data: DisplayAd[];
  setSelectedDisplayAd: React.Dispatch<React.SetStateAction<DisplayAd | null>>;
  setShowDisplayAdModal: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const columnHelper = createColumnHelper<DisplayAd>();
  const navigate = useNavigate();
  const { userModel } = useAppSelector((state) => state.user);

  // === Active-only UI toggle ===
  const [activeOnly, setActiveOnly] = useState<boolean>(true);

  useEffect(() => {
    if (userModel && userModel.userLevelId !== UserLevelNames.SuperAdmin) {
      toast.error(
        "You do not have permission to access this page. Please contact an administrator if you believe this is an error."
      );
      navigate("/");
    }
  }, [userModel, navigate]);

  // -- helpers --
  const parseDate = (v?: string | null): Date | null => {
    if (!v) return null;
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  };
  const toDateOnly = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const isActive = (ad: DisplayAd): boolean => {
    const today = toDateOnly(new Date());
    const start = parseDate(ad.startDate);
    const end = parseDate(ad.endDate);
    const sOk = !start || toDateOnly(start) <= today;
    const eOk = !end || toDateOnly(end) >= today;
    return sOk && eOk;
  };

  const displayData = useMemo(
    () => (activeOnly ? data.filter(isActive) : data),
    [activeOnly, data]
  );

  const columns = [
    columnHelper.accessor((row) => row.id, {
      id: "id",
      header: "",
      cell: (info) => (
        <div className="flex justify-center gap-2">
          <Tooltip content="View Member">
            <Link to={`/member/${info.row.original.memberId}`}>
              <Button type="button" className="buttonMain p-2" size={20}>
                <HiEye />
              </Button>
            </Link>
          </Tooltip>
          <Tooltip content="Edit Ad">
            <Button
              onClick={() => {
                setSelectedDisplayAd(info.row.original);
                setShowDisplayAdModal(true);
              }}
              className="buttonMain p-2"
              size={20}
            >
              <HiPencilAlt />
            </Button>
          </Tooltip>
        </div>
      ),
      size: 80,
      enableColumnFilter: false,
    }),
    columnHelper.accessor((row) => row.member?.name, {
      id: "memberId",
      header: "Member",
      cell: (info) => (
        <i>{info.getValue() ? info.row.original.member?.name : "EMDA"}</i>
      ),
    }),
    columnHelper.accessor((row) => row.name, {
      id: "name",
      header: "Name",
    }),
    columnHelper.accessor((row) => row.sortOrder, {
      id: "sortOrder",
      header: "Sort Order",
    }),
    columnHelper.accessor((row) => row.startDate, {
      id: "startDate",
      header: "Start Date",
      cell: (info) =>
        info.getValue() === null
          ? ""
          : displayDateOnly(new Date(info.getValue()!)),
    }),
    columnHelper.accessor((row) => row.endDate, {
      id: "endDate",
      header: "End Date",
      cell: (info) =>
        info.getValue() === null
          ? ""
          : displayDateOnly(new Date(info.getValue()!)),
    }),
  ];

  const table = useReactTable({
    data: displayData,
    columns,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 20 },
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
  });

  return (
    <>
      <div className="w-full mx-auto overflow-x-auto">
        {/* Active-only toggle */}
        <div className="flex justify-start items-center mb-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="activeOnly"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.currentTarget.checked)}
            />
            <label htmlFor="activeOnly" className="text-sm">
              Show active ads only
            </label>
          </div>
        </div>

        <PaginatedTable
          table={table}
          dataCount={displayData.length}
          columnResizeMode="onChange"
          showFilters={true}
        />
      </div>
    </>
  );
}
